import {Vector3, Quaternion, Matrix4} from 'three';


/**
 * Crée un vecteur à partir de deux points.
 */
export function makeVector(point1, point2) {
    return [point2[0]-point1[0], point2[1]-point1[1], point2[2]-point1[2]];
}

/**
 * Permet de normaliser un vecteur.
 */
export function normalizeVector(vector) {
    const norm = getVectorNorm(vector);
    return vector.map(val => val / norm);
}

/**
 * Calcule le produit scalaire de deux vecteurs.
 */
export function productScalare(Vec, VecMalloc){
    let out_dScalar;

    out_dScalar = Vec[0] * VecMalloc[0] +  Vec[1] * VecMalloc[1] + Vec[2] * VecMalloc[2];

    return out_dScalar;
}


/**
 * Projection d'un vecteur sur un autre vecteur orthogonal.
 */
export function projectVector(orthogonalVector, vectorToProject) {
    if (!orthogonalVector || !vectorToProject || orthogonalVector.length !== vectorToProject.length || (orthogonalVector.length !== 2 && orthogonalVector.length !== 3)) {
        throw new Error("Invalid arguments");
    }

    // Normalisation du vecteur normal
    const normalizedNormal = normalizeVector(orthogonalVector);

    // Calcul du produit scalaire
    const componentOnNormal = productScalare(normalizedNormal, vectorToProject);

    // Soustraction pour obtenir la projection
    return [vectorToProject[0]-componentOnNormal*normalizedNormal[0], vectorToProject[1]-componentOnNormal*normalizedNormal[1], vectorToProject[2]-componentOnNormal*normalizedNormal[2]];
}



/**
 * Calcule la norme d'un vecteur.
 */
export function getVectorNorm(vector) {
    return Math.sqrt(vector.reduce((sum, val) => sum + val * val, 0));
}


/**
 * Calcule la projection orthogonale d'un point sur une droite définie par un vecteur et un point d'origine.
 * @param {number[]} origin - Coordonnées de l'origine de la droite [x, y, z].
 * @param {number[]} point - Point à projeter [x, y, z].
 * @param {number[]} directionVector - Vecteur directeur de la droite [x, y, z].
 * @returns {number[]} - Coordonnées du point projeté [x, y, z].
 */
export function getOrthogonalProjection(origin, point, directionVector) {
    const vectorOriginToPoint = makeVector(origin, point); // Calcul du vecteur OA

    // Produit scalaire entre OA et le vecteur directeur
    const dotProductNumerator = productScalare(vectorOriginToPoint, directionVector);
    const dotProductDenominator = productScalare(directionVector, directionVector);
    const scalarFactor = dotProductNumerator / dotProductDenominator;

    return [
        origin[0] + directionVector[0] * scalarFactor,
        origin[1] + directionVector[1] * scalarFactor,
        origin[2] + directionVector[2] * scalarFactor
    ];
}

/**
 * Retourne un point décalé selon un vecteur et une distance donnée.
 * @param {number[]} point - Point de départ [x, y, z].
 * @param {number[]} vector - Vecteur de déplacement [x, y, z].
 * @param {number} distance - Distance de déplacement.
 * @param {boolean} inverse - Si true, applique un déplacement dans la direction opposée.
 * @returns {number[]} - Nouveau point après déplacement [x, y, z].
 */
export function getPointWithPointVecteurAndDistance(point, vector, distance, inverse) {
    if (inverse) {
        return [point[0]-vector[0]*distance, point[1]-vector[1]*distance, point[2]-vector[2]*distance];
    }
    else return [point[0]+vector[0]*distance, point[1]+vector[1]*distance, point[2]+vector[2]*distance];
}


/**
 * Calcule la distance entre deux points.
 */
export function calculateDistance(pointA, pointB) {
    const dx = pointB[0] - pointA[0];
    const dy = pointB[1] - pointA[1];
    const dz = pointB[2] - pointA[2];

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calcule l'angle entre deux vecteurs en degrés.
 */
export function calculateAngleBetweenVectors(vector1, vector2, planeAxis, isSignWanted) {
    const angleRad = calculateAngleBetweenVectorsRad(vector1, vector2, isSignWanted, planeAxis);
    return radiansToDegrees(angleRad);
}


/**
 * Calcule l'angle entre deux vecteurs en radians.
 */
function calculateAngleBetweenVectorsRad(vector1, vector2, isSignWanted, planeAxis) {
    const scalarProduct = productScalare(vector1, vector2)
    const norm1 = getVectorNorm(vector1)
    const norm2 = getVectorNorm(vector2)

    if (norm1 === 0 || norm2 === 0) {
        return 0;
    }

    let angleRad = Math.acos(scalarProduct / (norm1 * norm2));

    if (isSignWanted && planeAxis) {
        const crossProduct = [
            vector1[1] * vector2[2] - vector1[2] * vector2[1],
            vector1[2] * vector2[0] - vector1[0] * vector2[2],
            vector1[0] * vector2[1] - vector1[1] * vector2[0]
        ];
        const sign = crossProduct.reduce((sum, val, i) => sum + val * planeAxis[i], 0) < 0 ? -1 : 1;
        angleRad *= sign;
    }

    return angleRad;
}


/**
 * Convertit un angle radians en degrés.
 * @param {number} radians - Une valeur en radians qu'on convertit en degré.
 * @return {number} - Une valeur en degré.
 */
export function radiansToDegrees(radians) {
    return radians * (180 / Math.PI);
}

export function degreesToRadians(degrees) {
    return degrees*(Math.PI/180);
}



/**
 * Retourne la direction d'un ArrowHelper après transformation (rotation globale appliquée).
 * @param {Object} arrow - L'objet ArrowHelper dont on veut connaître la direction après transformation.
 * @returns {number[]} - Vecteur direction sous forme de tableau [x, y, z].
 */
export function getTransformedDirection(arrow) {
    const initialDirection = new Vector3(0, 1, 0); // Direction initiale de l'ArrowHelper

    // Récupération de la rotation globale de l'objet
    const globalRotation = new Quaternion();
    arrow.getWorldQuaternion(globalRotation);

    // Application de la rotation globale sur le vecteur direction
    initialDirection.applyQuaternion(globalRotation);

    return [initialDirection.x, initialDirection.y, initialDirection.z];
}


/**
 * Retourne la position d'une sphère après transformation (position globale dans la scène).
 * @param {Object} sphere - L'objet dont on veut connaître la position après transformation.
 * @returns {number[]} - Position sous forme de tableau [x, y, z].
 */
export function getTransformedPosition(sphere) {
    const globalPosition = new Vector3();

    // Récupération de la position globale
    sphere.getWorldPosition(globalPosition);

    return [globalPosition.x, globalPosition.y, globalPosition.z];
}



/**
 * Calcule l'angle nécessaire pour faire tourner un vecteur autour d'un axe donné.
 * @param {number[]} RotationAxis - Axe de rotation [x, y, z].
 * @param {number[]} PlaneNormal - Normal du plan de projection [x, y, z].
 * @param {number[]} VectorToRotate - Vecteur à faire tourner [x, y, z].
 * @param {number} angle - Angle de rotation en degrés.
 * @returns {number} - L'angle entre le vecteur initial et le vecteur tourné après rotation.
 */
export function computeAngleToRotate(RotationAxis, PlaneNormal, VectorToRotate, angle){

    let VectorToRotateProjected = projectVector(PlaneNormal, VectorToRotate);
    let OriginPoint = new Vector3(0,0,0);
    let OriginTranslated = new Vector3(VectorToRotateProjected[0], VectorToRotateProjected[1], VectorToRotateProjected[2]);
    let PlaneNormalise = normalizeVector(PlaneNormal);
    const Plane= new Vector3(PlaneNormalise[0], PlaneNormalise[1], PlaneNormalise[2]);

    let rad = degreesToRadians(angle)
    OriginTranslated.sub(OriginPoint);
    let rotationMatrix = new Matrix4();
    rotationMatrix.makeRotationAxis(Plane, rad);

    OriginTranslated.applyMatrix4(rotationMatrix);
    OriginTranslated.add(OriginPoint);

    let linePoint = [OriginTranslated.x, OriginTranslated.y, OriginTranslated.z];
    let targetPointForRotation = computeIntersectionLinePlane([0,0,0], RotationAxis, linePoint, PlaneNormal ).intersectionPoint;

    return calculateAngleBetweenVectors(VectorToRotate, targetPointForRotation, RotationAxis, true);


}


/**
 * Calcule l'intersection entre une ligne et un plan.
 * @param {number[]} planePoint - Point situé sur le plan [x, y, z].
 * @param {number[]} planeNormal - Vecteur normal du plan [x, y, z].
 * @param {number[]} linePoint - Point situé sur la ligne [x, y, z].
 * @param {number[]} lineDirection - Vecteur directeur de la ligne [x, y, z].
 * @param {number} tolerance - Tolérance pour les comparaisons numériques (défaut: 1e-6).
 * @returns {Object} - Objet contenant le code d'erreur, le point d'intersection (ou null), et le paramètre t.
 */
export function computeIntersectionLinePlane(planePoint, planeNormal, linePoint, lineDirection, tolerance = 1e-6) {

    let lineDirNorm = normalizeVector(lineDirection);
    let planeNormalNorm = normalizeVector(planeNormal);

    let psDirNormal = productScalare(lineDirNorm, planeNormalNorm);
    let psLinePointNormal = productScalare(linePoint, planeNormalNorm);

    // Calculer le D du plan (-d)
    let psPlanePointNormal = productScalare(planePoint, planeNormalNorm);
    let numerator = psPlanePointNormal - psLinePointNormal;

    // Si le produit scalaire est proche de zéro, cela signifie que la ligne et le plan sont parallèles
    if (Math.abs(psDirNormal) < tolerance) {
        // Si le numérateur est également proche de zéro, la ligne est dans le plan
        if (Math.abs(numerator) < tolerance) {
            return { errorCode: 'LineInPlane', intersectionPoint: null };
        } else {
            // Si le numérateur est non nul, il n'y a pas d'intersection
            return { errorCode: 'NoIntersection', intersectionPoint: null };
        }
    }

    // Calculer le paramètre t où la ligne intersecte le plan
    let t = numerator / psDirNormal;

    // Calculer le point d'intersection
    let intersectionPoint = [
        linePoint[0] + lineDirNorm[0] * t,
        linePoint[1] + lineDirNorm[1] * t,
        linePoint[2] + lineDirNorm[2] * t
    ];

    return { errorCode: 'IntersectionFound', intersectionPoint: intersectionPoint, t: t };
}
